/**
 * ChainMonitoringCompact.js
 *
 * Versão simplificada do ChainVisualization para o Executive Dashboard
 * Exibe visualização compacta da cadeia com métricas essenciais
 */

import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";

// Context
import AuthContext from "connections/auth/AuthProvider.js";

// API
import { getChainMonitoringData } from "../../viewServices/apiCalls";

// Utils do ChainVisualization (reutilizados)
import { assembleChainVisualizationData } from "../../../Cockpit/viewServices/assemblers/ChainVisualizationAssembler";
import { updateChainDataWithRelativity } from "../../../Cockpit/viewServices/assemblers/ChainVisualizationRelativityCalculator";

// Utils locais
import {
  adaptChainDataForCompactView,
  selectDefaultAnchor,
  formatValue,
  formatPercentage,
} from "./chainMonitoringUtils";

// Shared components
import {
  CardLoadingState,
  CardErrorState,
  CardEmptyState,
  CardNoDataState,
} from "../shared";

const MIN_HEIGHT = 180;

// ============================================================================
// STYLES
// ============================================================================

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.75),
    height: "100%",
    minHeight: "180px",
    maxHeight: "400px",
    overflow: "hidden",
  },
  tracksContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
    overflowY: "auto",
    overflowX: "hidden",
    flex: 1,
    paddingRight: theme.spacing(0.5),
    // Estilo da scrollbar
    "&::-webkit-scrollbar": {
      width: "6px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "#f1f1f1",
      borderRadius: "3px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#888",
      borderRadius: "3px",
      "&:hover": {
        backgroundColor: "#555",
      },
    },
  },
  trackRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.5),
  },
  trackLabel: {
    flex: "0 0 85px",
    padding: theme.spacing(0.5, 0.75),
    backgroundColor: (props) => props.clientColor || "#1976d2",
    color: "#fff",
    fontWeight: 600,
    fontSize: "0.7rem",
    textAlign: "center",
    borderRadius: theme.shape.borderRadius,
    textTransform: "capitalize",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "32px",
  },
  stepBox: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.375, 0.75),
    backgroundColor: "transparent",
    border: "1px solid #e0e0e0",
    borderRadius: theme.shape.borderRadius,
    minHeight: "32px",
  },
  stepBoxSellin: {
    flex: "0 0 100px",
  },
  stepBoxChannel: {
    flex: "0 0 100px",
  },
  stepBoxSellout: {
    flex: "0 0 130px",
  },
  metricItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(0.125),
    flex: 1,
  },
  metricLabel: {
    fontSize: "0.5625rem",
    color: "#757575",
    textTransform: "lowercase",
    fontWeight: 500,
    lineHeight: 1,
  },
  metricValue: {
    fontSize: "0.6875rem",
    color: "#212121",
    fontWeight: 600,
    lineHeight: 1,
  },
  arrow: {
    fontSize: "0.875rem",
    color: "#bdbdbd",
    flex: "0 0 auto",
  },
  divider: {
    width: "1px",
    height: "20px",
    backgroundColor: "#e0e0e0",
  },
}));

// ============================================================================
// SUB-COMPONENTES INTERNOS
// ============================================================================

/**
 * MetricItem - Exibe uma métrica individual (label + value)
 */
function MetricItem({ label, value, classes }) {
  return (
    <Box className={classes.metricItem}>
      <Typography className={classes.metricLabel}>{label}</Typography>
      <Typography className={classes.metricValue}>{value}</Typography>
    </Box>
  );
}

MetricItem.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  classes: PropTypes.object.isRequired,
};

/**
 * StepBox - Exibe um box de step com métricas
 */
function StepBox({ type, metrics, classes }) {
  const boxClass = `${classes.stepBox} ${classes[`stepBox${type.charAt(0).toUpperCase() + type.slice(1)}`]}`;

  return (
    <Box className={boxClass}>
      {type === "sellin" && (
        <>
          <MetricItem
            label="discount"
            value={formatPercentage(metrics.discount)}
            classes={classes}
          />
          <Box className={classes.divider} />
          <MetricItem
            label="price"
            value={formatValue(metrics.price)}
            classes={classes}
          />
        </>
      )}

      {type === "channel" && (
        <>
          <MetricItem
            label="margin"
            value={formatPercentage(metrics.margin)}
            classes={classes}
          />
          <Box className={classes.divider} />
          <MetricItem
            label="price"
            value={formatValue(metrics.price)}
            classes={classes}
          />
        </>
      )}

      {type === "sellout" && (
        <>
          <MetricItem
            label="margin"
            value={formatPercentage(metrics.margin)}
            classes={classes}
          />
          <Box className={classes.divider} />
          <MetricItem
            label="price"
            value={formatValue(metrics.price)}
            classes={classes}
          />
          <Box className={classes.divider} />
          <MetricItem
            label="relativity"
            value={formatPercentage(metrics.relativity)}
            classes={classes}
          />
        </>
      )}
    </Box>
  );
}

StepBox.propTypes = {
  type: PropTypes.oneOf(["sellin", "channel", "sellout"]).isRequired,
  metrics: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
};

/**
 * TrackRow - Exibe uma linha completa de uma track
 * Renderização varia conforme topology:
 * - Indireto: Sell-in → Channel → Sellout (3 boxes)
 * - Direto: Sell-in → Sellout (2 boxes)
 */
function TrackRow({ track, classes }) {
  const isIndirect = track.topology === "indireto";

  return (
    <Box className={classes.trackRow}>
      {/* Label da Track */}
      <Box className={classes.trackLabel}>{track.label}</Box>

      {/* Step 1 (Sell-in) - SEMPRE */}
      <StepBox
        type="sellin"
        metrics={{ discount: track.step1.discount, price: track.step1.price }}
        classes={classes}
      />

      {/* Seta */}
      <ArrowForwardIcon className={classes.arrow} />

      {/* Step 2/3 (Channel) - APENAS INDIRETO */}
      {isIndirect && (
        <>
          <StepBox
            type="channel"
            metrics={{
              margin: track.channel.margin,
              price: track.channel.price,
            }}
            classes={classes}
          />

          {/* Seta */}
          <ArrowForwardIcon className={classes.arrow} />
        </>
      )}

      {/* Step 3 Final (Sellout) - SEMPRE */}
      <StepBox
        type="sellout"
        metrics={{
          margin: track.step3.margin,
          price: track.step3.price,
          relativity: track.step3.relativity,
        }}
        classes={classes}
      />
    </Box>
  );
}

TrackRow.propTypes = {
  track: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
};

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export default function ChainMonitoringCompact({
  programId,
  filters = {},
  maxTracksToShow = 999,
}) {
  // Context
  const { userDetails } = useContext(AuthContext);
  const clientColor = userDetails?.implementation_color || "#1976d2";

  // Styles
  const classes = useStyles({ clientColor });

  // State
  const [tracks, setTracks] = useState([]);
  const [isLoading, setIsLoading] = useState(!!programId);
  const [error, setError] = useState(null);

  // ============================================================================
  // FETCH E PROCESSAMENTO DE DADOS
  // ============================================================================

  useEffect(() => {
    const fetchAndProcessData = async () => {
      if (!programId) {
        setIsLoading(false);
        setTracks([]);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        // 1. Fetch dados brutos da API (usa endpoint próprio do Executive)
        const response = await getChainMonitoringData(
          programId, // programId como primeiro parâmetro
          filters, // filters como segundo parâmetro
          "input_sellthrough_volume_sales", // weightField como terceiro parâmetro
          "", // consideredRoutes como quarto parâmetro
        );

        // Response has data_by_type (actual/forecast/policy)
        const dataByType = response.data_by_type || {};

        // Use actual data for now
        const actualData = dataByType.actual;

        if (!actualData || !actualData.data) {
          setTracks([]);
          return;
        }

        // 2. Processar dados com assembler (reutiliza lógica do ChainVisualization)
        const assembledData = assembleChainVisualizationData(actualData.data);

        // 3. Selecionar âncora padrão automaticamente
        const defaultAnchor = selectDefaultAnchor(assembledData);

        // 4. Aplicar relatividade (reutiliza lógica do ChainVisualization)
        let dataWithRelativity = assembledData;
        if (defaultAnchor) {
          dataWithRelativity = updateChainDataWithRelativity(
            assembledData,
            defaultAnchor,
            "input_sellthrough_volume_sales",
          );
        }

        // 5. Adaptar para formato compacto
        const compactTracks = adaptChainDataForCompactView(
          dataWithRelativity,
          maxTracksToShow,
        );

        setTracks(compactTracks);
      } catch (err) {
        console.error("Error fetching chain monitoring data:", err);
        setError(err.message || "Failed to load chain monitoring data");
      } finally {
        setIsLoading(false);
      }
    };

    fetchAndProcessData();
  }, [programId, filters, maxTracksToShow]);

  // ============================================================================
  // RENDER
  // ============================================================================

  // Loading state
  if (isLoading) {
    return <CardLoadingState minHeight={MIN_HEIGHT} />;
  }

  // Error state
  if (error) {
    return <CardErrorState message={error} minHeight={MIN_HEIGHT} />;
  }

  // Empty state (no program selected)
  if (!programId) {
    return <CardEmptyState minHeight={MIN_HEIGHT} />;
  }

  // No data state
  if (!tracks || tracks.length === 0) {
    return (
      <CardNoDataState
        title="Nenhuma track disponível"
        subtitle="Verifique os dados de cadeia"
        minHeight={MIN_HEIGHT}
      />
    );
  }

  return (
    <Box className={classes.container}>
      <Box className={classes.tracksContainer}>
        {tracks.map((track, index) => (
          <TrackRow key={track.id || index} track={track} classes={classes} />
        ))}
      </Box>
    </Box>
  );
}

ChainMonitoringCompact.propTypes = {
  programId: PropTypes.number,
  filters: PropTypes.object,
  maxTracksToShow: PropTypes.number,
};
